import { EditOutlined, FileTextOutlined, LinkOutlined } from '@ant-design/icons'
import { Button, Input, Popover, Select, Tooltip } from 'antd'
import { useLinkedDocuments } from 'features/files'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import {
  FieldValueSource,
  SourceType,
} from 'features/libra/ontology/ontologyFieldTypes'
import isNil from 'lodash/isNil'
import { FC, ReactNode, useMemo, useState } from 'react'
import styled from 'styled-components'

type DocumentSource = {
  type: SourceType.DOCUMENT
  value: string
  documentName?: string
  page?: number | null
  excerpt?: string | null
}

function isDocumentSource(source: FieldValueSource): source is DocumentSource {
  return (
    source !== null &&
    source.type === SourceType.DOCUMENT &&
    Boolean(source.value)
  )
}

function SourceCitationCard({
  source,
  onNavigate,
}: {
  source: DocumentSource
  onNavigate: () => void
}) {
  const docName = source.documentName || source.value
  return (
    <div style={{ maxWidth: 320 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {docName}
        {source.page !== null ? `, page ${source.page}` : ''}
      </div>
      {source.excerpt && (
        <div style={{ fontStyle: 'italic', marginBottom: 8 }}>
          &ldquo;{source.excerpt}&rdquo;
        </div>
      )}
      <Button
        type='link'
        size='small'
        icon={<LinkOutlined />}
        onClick={onNavigate}
        style={{ padding: 0 }}>
        Navigate to source
      </Button>
    </div>
  )
}

/** Hover popover on the source display with citation details and navigation. */
export const DocumentSourcePopover: FC<{
  source: FieldValueSource
  children: ReactNode
}> = ({ source, children }) => {
  const { setSelectedDocumentId } = useLibraDocument()

  if (!isDocumentSource(source)) {
    return <>{children}</>
  }

  return (
    <Popover
      trigger='hover'
      mouseLeaveDelay={0.2}
      content={
        <SourceCitationCard
          source={source}
          onNavigate={() =>
            setSelectedDocumentId(source.value, source.page ?? undefined)
          }
        />
      }>
      <PopoverTriggerWrap>{children}</PopoverTriggerWrap>
    </Popover>
  )
}

/** Compact source chip for contexts without a SourceInput (e.g. NII Screening edit). */
export const DocumentSourceIndicator: FC<{ source: FieldValueSource }> = ({
  source,
}) => {
  if (!isDocumentSource(source)) {
    return null
  }

  const label = source.documentName || source.value

  return (
    <DocumentSourcePopover source={source}>
      <SourceIndicatorChip title={label}>
        <FileTextOutlined />
        <span>Source</span>
      </SourceIndicatorChip>
    </DocumentSourcePopover>
  )
}

interface ReadOnlySourceDisplayProps {
  source: FieldValueSource
}

/**
 * Non-interactive display of a field's source metadata for read-only contexts.
 * Pair with DocumentSourcePopover for citation and navigation on hover.
 */
export const ReadOnlySourceDisplay: FC<ReadOnlySourceDisplayProps> = ({
  source,
}) => {
  if (!source) return null

  const isDocument = source.type === SourceType.DOCUMENT
  const icon = isDocument ? <FileTextOutlined /> : <EditOutlined />
  const label = isDocument ? source.documentName || source.value : source.value

  return (
    <ReadOnlySourceContainer>
      <SourceLabel>Source:</SourceLabel>
      <SourceDisplay>
        {icon}
        <ReadOnlySourceText title={label}>{label}</ReadOnlySourceText>
      </SourceDisplay>
    </ReadOnlySourceContainer>
  )
}

const ReadOnlySourceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
`

const ReadOnlySourceText = styled.span`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SourceDisplay = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`

const PopoverTriggerWrap = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: help;
`

const SourceIndicatorChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  cursor: default;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
`

interface SourceInputProps {
  source: FieldValueSource
  onSourceChange: (newSource: FieldValueSource) => void
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
`

const StyledSelect = styled(Select)`
  flex: 1 1 auto;
  min-width: 0;
`

const StyledInput = styled(Input)`
  flex: 1 1 auto;
  min-width: 0;
`

const SourceLabel = styled.span`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
  white-space: nowrap;
  flex: 0 0 auto;
`

export const SourceInput: FC<SourceInputProps> = ({
  source,
  onSourceChange,
}) => {
  const { unitId } = useLibraDocument()
  const [isTypeDocument, setIsTypeDocument] = useState(
    source?.type === SourceType.DOCUMENT
  )
  const isDocumentMode = source
    ? source.type === SourceType.DOCUMENT
    : isTypeDocument

  const { documents, isLoading } = useLinkedDocuments(
    useMemo(() => (unitId ? { unit: unitId } : undefined), [unitId])
  )

  const documentOptions = useMemo(() => {
    return documents.map((doc) => ({
      value: doc.entry_id,
      label: doc.filename || doc.visible_name || doc.entry_id,
    }))
  }, [documents])

  const handleDocumentSelect = (value: unknown) => {
    if (isNil(value)) {
      onSourceChange(null)
      return
    }

    const documentId = value as string
    const selectedDoc = documents.find((doc) => doc.entry_id === documentId)
    onSourceChange({
      type: SourceType.DOCUMENT,
      value: documentId,
      documentName: selectedDoc?.filename || selectedDoc?.visible_name,
    })
  }

  const handleFreeTextChange = (text: string) => {
    if (isNil(text) || text.trim() === '') {
      onSourceChange(null)
      return
    }

    onSourceChange({
      type: SourceType.FREETEXT,
      value: text,
    })
  }

  const toggleMode = () => {
    setIsTypeDocument(!isTypeDocument)
    // Clear source when toggling
    onSourceChange(null)
  }

  return (
    <Container>
      <SourceLabel>Source:</SourceLabel>
      {isDocumentMode ? (
        <>
          <StyledSelect
            size='small'
            placeholder='Select document...'
            loading={isLoading}
            value={
              source?.type === SourceType.DOCUMENT ? source.value : undefined
            }
            onChange={handleDocumentSelect}
            options={documentOptions}
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <Tooltip title='Switch to free text'>
            <Button size='small' icon={<EditOutlined />} onClick={toggleMode} />
          </Tooltip>
        </>
      ) : (
        <>
          <StyledInput
            size='small'
            placeholder='Enter source description...'
            value={source?.type === SourceType.FREETEXT ? source.value : ''}
            onChange={(e) => handleFreeTextChange(e.target.value)}
          />
          <Tooltip title='Switch to document selection'>
            <Button
              size='small'
              icon={<FileTextOutlined />}
              onClick={toggleMode}
            />
          </Tooltip>
        </>
      )}
    </Container>
  )
}
